/**
 * Store Management Context
 *
 * Provides store and company info to all store management pages.
 * Hydrated from URL params (:companyId, :storeId) on mount.
 * Exposes allStores for the sidebar store selector.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyService } from '@/services/company.service';
import { storeService } from '@/services/store.service';
import type { Store } from '@/types/store.types';
import type { Company } from '@/types/company.types';

// ============================================================================
// TYPES
// ============================================================================

interface StoreManagementContextType {
  companyId: string;
  storeId: string;
  store: Store | null;
  company: Company | null;
  allStores: Store[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  switchStore: (newStoreId: string) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const StoreManagementContext = createContext<StoreManagementContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface StoreManagementProviderProps {
  children: ReactNode;
}

export function StoreManagementProvider({ children }: StoreManagementProviderProps) {
  const { companyId = '', storeId = '' } = useParams<{ companyId: string; storeId: string }>();
  const navigate = useNavigate();
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [store, setStore] = useState<Store | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!companyId || !storeId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [companyData, storeData, storesData] = await Promise.all([
        companyService.getById(companyId),
        // La sucursal se pide **por su id**. Antes se buscaba dentro de la
        // primera página del listado (`limit: 100`, ordenado por nombre), así
        // que en una cadena con más de cien locales los del final salían como
        // «Sucursal no encontrada» — existiendo y siendo accesibles. Un
        // listado paginado no sirve de índice.
        storeService.getById(storeId),
        // El listado sigue haciendo falta para el selector de sucursal.
        companyService.getStores(companyId, { limit: 100 }),
      ]);

      setCompany(companyData);
      setAllStores(storesData.data);

      // La URL lleva los dos ids: hay que comprobar que la sucursal es de esa
      // compañía, o el contexto mostraría el nombre de una y los datos de otra.
      if (storeData.companyId !== companyId) {
        setError('Sucursal no encontrada');
        setStore(null);
        return;
      }

      setStore(storeData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cargar datos';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, storeId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const switchStore = useCallback(
    (newStoreId: string) => {
      // Navigate to the same manage section but with the new store
      const currentPath = window.location.pathname;
      const manageSection = currentPath.split('/manage/')[1]?.split('/')[0] || 'menu';
      navigate(`/my-companies/${companyId}/stores/${newStoreId}/manage/${manageSection}`);
    },
    [companyId, navigate],
  );

  const value = useMemo(
    () => ({
      companyId,
      storeId,
      store,
      company,
      allStores,
      isLoading,
      error,
      refresh: fetchData,
      switchStore,
    }),
    [companyId, storeId, store, company, allStores, isLoading, error, fetchData, switchStore],
  );

  return (
    <StoreManagementContext.Provider value={value}>
      {children}
    </StoreManagementContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useStoreManagement(): StoreManagementContextType {
  const context = useContext(StoreManagementContext);
  if (!context) {
    throw new Error('useStoreManagement must be used within StoreManagementProvider');
  }
  return context;
}
